import React from 'react';

export const buildErrorReport = (error, info) =>
  [
    `Time: ${new Date().toISOString()}`,
    `URL: ${window.location.href}`,
    `Browser: ${navigator.userAgent}`,
    '',
    `Error: ${error?.message || String(error)}`,
    '',
    error?.stack || '',
    '',
    'Component stack:',
    info?.componentStack || '',
  ].join('\n');

const copyText = async (text) => {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (e) {}
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.select();
  let ok = false;
  try {
    ok = document.execCommand('copy');
  } catch (e) {
    ok = false;
  }
  document.body.removeChild(textarea);
  return ok;
};

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null, copied: false };
    this.onCopy = this.onCopy.bind(this);
    this.onReload = this.onReload.bind(this);
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    this.setState({ info });
    console.error('Unhandled render error', error, info);
  }

  async onCopy() {
    const { error, info } = this.state;
    const copied = await copyText(buildErrorReport(error, info));
    this.setState({ copied });
  }

  onReload() {
    window.location.reload();
  }

  render() {
    const { error, info, copied } = this.state;
    const { children } = this.props;

    if (!error) return children;

    const report = buildErrorReport(error, info);

    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 p-6">
        <div className="bg-white rounded shadow-lg w-full max-w-4xl p-8">
          <h1 className="text-2xl font-semibold text-gray-800 mb-2">Something went wrong</h1>
          <p className="text-gray-600 mb-4">
            The page crashed. Copy the details below and send them to support, then reload the page.
            Unsaved changes may be lost.
          </p>
          <div className="text-red-600 font-semibold mb-3 break-words">
            {error?.message || String(error)}
          </div>
          <textarea
            readOnly
            value={report}
            className="w-full h-64 bg-gray-100 text-gray-800 text-xs font-mono p-3 rounded border border-gray-300 mb-4"
          />
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={this.onCopy}
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded"
            >
              {copied ? 'Copied' : 'Copy details'}
            </button>
            <button
              type="button"
              onClick={this.onReload}
              className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-2 px-4 rounded"
            >
              Reload page
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
